<template>
  <div class="layout">
    <nav-bar :logoSrc="$maxxpm.config.logo.src" :logoAlt="$maxxpm.config.logo.alt"
      :bannerTitle="$maxxpm.config.nav.bannerTitle" :bannerSubtitle="$maxxpm.config.nav.bannerSubtitle"
      :bannerCTA="$maxxpm.config.nav.bannerCTA" />
    <div v-if="promoItems.length" id="incentive" class="promo-carousel">
      <div class="promo-carousel-content">
        <p class="promo-message">{{ promoMessage }}</p>
        <Button label="View Availability" class="promo-cta" @click="router.push('/availability')" />
      </div>
      <div v-if="promoItems.length > 1" class="promo-dots">
        <button v-for="(promo, index) in promoItems" :key="promo.id" type="button" class="promo-dot"
          :class="{ active: index === activePromoIndex }" :aria-label="`Promotion ${index + 1}`"
          @click="setPromoIndex(index)" />
      </div>
      <p class="promo-disclaimer">
        *Offer subject to availability. Contact leasing for more details.
      </p>
    </div>
    <main>
      <router-view />
      <Dialog v-model:visible="showPopup" :modal="true" :dismissableMask="true" :style="{ width: '50vw' }"
        :breakpoints="{ '960px': '75vw', '641px': '95vw' }">
        <template #header>
          <h2 class="text-center flex-grow-1">
            Don't wait, schedule to visit the property today!
          </h2>
        </template>
        <div class="flex justify-content-center p-4 popup-showing">
          <!-- <Button
            @click="
              () => {
                Calendly.initPopupWidget({
                  url: $maxxpm.config.calendlyLink,
                });
              }
            "
            aria-haspopup="dialog"
            label="Schedule a Showing"
          ></Button> -->
          <a :href="$maxxpm.config.showingsLink" target="_blank">
            <Button aria-haspopup="dialog" label="Schedule a Showing"></Button>
          </a>
        </div>
      </Dialog>
    </main>
    <footer>
      <app-footer :communityName="$maxxpm.config.communityName" :streetAddress="$maxxpm.config.footer.address"
        :cityStateZip="$maxxpm.config.footer.cityStateZip" :phone="$maxxpm.config.footer.phone"
        :logoUrl="$maxxpm.config.footer.logo ?? $maxxpm.config.logo.src"
        :copyrightYear="$maxxpm.config.footer.copyrightYear" />
    </footer>
  </div>
</template>

<script setup>
import { inject, ref, onMounted, onUnmounted, computed } from "vue";
import { useRouter } from "vue-router";
import { event } from "vue-gtag";
import axios from "axios";
import Dialog from "primevue/dialog";
import Button from "primevue/button";

const $maxxpm = inject("$maxxpm");
const router = useRouter();

const promoItems = ref([]);
const activePromoIndex = ref(0);
const isMobile = ref(window.innerWidth <= 768);
let promoInterval = null;

const promoMessage = computed(() => {
  const promo = promoItems.value[activePromoIndex.value];
  if (!promo) return "";
  return isMobile.value ? promo.badge : promo.description;
});

function dedupePromotions(listings) {
  const byId = new Map();
  for (const listing of listings) {
    for (const promo of listing.promotions ?? []) {
      if (promo?.id && !byId.has(promo.id)) {
        byId.set(promo.id, promo);
      }
    }
  }
  return Array.from(byId.values());
}

async function loadPromotions() {
  const apiUrl = process.env.VUE_APP_LISTINGS_API_URL;
  const propertyGroup = $maxxpm.config.availability?.propertyGroup;
  if (!apiUrl || !propertyGroup) return;

  try {
    const { data } = await axios.get(
      `${apiUrl}/listings?property_group=${propertyGroup}`
    );
    promoItems.value = dedupePromotions(data);
    if (promoItems.value.length > 1) {
      startPromoCarousel();
    }
  } catch {
    promoItems.value = [];
  }
}

function startPromoCarousel() {
  clearInterval(promoInterval);
  promoInterval = setInterval(() => {
    activePromoIndex.value =
      (activePromoIndex.value + 1) % promoItems.value.length;
  }, 5000);
}

function setPromoIndex(index) {
  activePromoIndex.value = index;
  if (promoItems.value.length > 1) {
    startPromoCarousel();
  }
}

function handleResize() {
  isMobile.value = window.innerWidth <= 768;
}

const showPopup = ref(false);
const Calendly = ref(null);

function isCalendlyEvent(e) {
  return (
    e.origin === "https://calendly.com" &&
    e.data.event &&
    e.data.event.indexOf("calendly.") === 0
  );
}

function calendlyMessageHandler(e) {
  if (isCalendlyEvent(e)) {
    switch (e.data.event) {
      case "calendly.event_type_viewed":
        event("agent_showing_viewed", {
          event_category: "tenants",
          event_label: "Home page popup",
        });
        break;
      case "calendly.date_and_time_selected":
        event("agent_showing_date_time_selected", {
          event_category: "tenants",
          event_label: "Home page popup",
        });
        break;
      case "calendly.event_scheduled":
        event("agent_showing_booked", {
          event_category: "tenants",
          event_label: e.data.payload,
        });
        break;
    }
  }
}

const calendlyObserver = new MutationObserver((mutationList) => {
  for (const mutation of mutationList) {
    if (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0) {
      const added = Array.from(mutation.addedNodes);
      const removed = Array.from(mutation.removedNodes);
      if (
        added.length > 0 &&
        added.some(
          (node) =>
            typeof node.classList !== "undefined" &&
            node.classList.contains("calendly-overlay")
        )
      ) {
        window.addEventListener("message", calendlyMessageHandler);
      }

      if (
        removed.length > 0 &&
        removed.some(
          (node) =>
            typeof node.classList !== "undefined" &&
            node.classList.contains("calendly-overlay")
        )
      ) {
        window.removeEventListener("message", calendlyMessageHandler);
        calendlyObserver.disconnect();
      }
    }
  }
});

onMounted(() => {
  window.addEventListener("resize", handleResize);
  loadPromotions();

  Calendly.value = window.Calendly;
  const cname = "showPopup";
  setTimeout(() => {
    if (
      $maxxpm.config.hasShowingsPopup &&
      !document.cookie.split("; ").find((c) => c.startsWith(cname))
    ) {
      // calendlyObserver.observe(document.body, {
      //   attributes: true,
      //   childList: true,
      //   subtree: true,
      // });
      document.cookie = `${cname}=true; max-age=${60 * 60 * 24 * 1}`;
      showPopup.value = true;
    }
  }, 5000);
});

onUnmounted(() => {
  clearInterval(promoInterval);
  window.removeEventListener("resize", handleResize);
});
</script>

<style>
#app {
  font-family: Roboto, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

.layout {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100vh;
  position: relative;
}

body {
  margin: 0;
}

.img-fluid {
  max-width: 100%;
  height: auto;
}

.promo-carousel {
  background: var(--primary-color);
  color: #fff;
  padding: 0.45rem 1rem 0.3rem;
}

.promo-carousel-content {
  max-width: min(1200px, 95vw);
  margin: 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.85rem;
  flex-wrap: wrap;
}

.promo-message {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  text-align: center;
}

.promo-carousel .promo-cta.p-button {
  background: #fff;
  color: var(--primary-color);
  font-weight: 700;
  padding: 0.35rem 0.85rem;
  border: none;
}

.promo-carousel .promo-cta.p-button:hover {
  background: #f5f5f5;
  color: var(--primary-color);
}

.promo-dots {
  display: flex;
  justify-content: center;
  gap: 0.4rem;
  margin-top: 0.35rem;
}

.promo-dot {
  width: 8px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.45);
  cursor: pointer;
}

.promo-dot.active {
  background: #fff;
}

.promo-disclaimer {
  margin: 0.25rem 0 0;
  text-align: center;
  font-size: 0.9rem;
  opacity: 0.95;
}

@media (max-width: 768px) {
  .promo-carousel-content {
    flex-direction: column;
  }

  .promo-disclaimer {
    font-size: 0.8rem;
  }
}
</style>
